import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import { DebounceInput } from "react-debounce-input";
import { toast } from "react-toastify";
import { LockIcon, UnlockIcon } from "../../../../assets/Icons";
import SelectDropdown from "../../components/ui/SelectDropdown";
import { AppConstants, AppStrings, formatNumber, parseNullableNumber } from "../../utils";
import { styles } from "./styles";

const AmountSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { calculator } = useAppSelector((state) => state.remittance);

  return (
    <div id="paymentOverview" className={styles.amountSection.wrapper}>
      <div className={styles.amountSection.heading}>{AppStrings.AMOUNT_SECTION.HEADING}</div>

      <div className={styles.amountSection.box}>
        {/* You Send */}
        <div id="youSend" className={styles.amountSection.youSendContainer}>
          <span className={styles.amountSection.youSendLabel}>
            {AppStrings.AMOUNT_SECTION.YOU_SEND_LABEL}
          </span>

          <div className={styles.amountSection.youSendRow}>
            <DebounceInput
              minLength={2}
              className={styles.amountSection.youSendInput}
              name="sendingAmount"
              type="text"
              maxLength={15}
              value={formatNumber(calculator?.amountCalculation?.user_send)}
              debounceTimeout={1000}
              onChange={(e: any) => {
                const newAmount = e.target.value.replace(/,/g, "");
                dispatch(
                  remittanceActions.setAmountCalculation({
                    user_send: parseNullableNumber(newAmount),
                    changeInputFrom: "send",
                  })
                );
              }}
              disabled={calculator?.amountCalculation?.lock_reciepient_recieve}
            />

            <div className={styles.amountSection.youSendCurrencyBox}>
              <img
                src={`/images/png/INR.png`}
                alt="country-image"
                className={styles.amountSection.currencyImg}
              />

              <span className="bg-white text-[19px]">{`INR`}</span>
            </div>
          </div>
        </div>

        {/* They Receive */}
        <div id="theyRecieve" className={styles.amountSection.theyReceiveContainer}>
          <span className={styles.amountSection.theyReceiveLabel}>
            {AppStrings.AMOUNT_SECTION.THEY_RECEIVE_LABEL}
          </span>

          <div className={styles.amountSection.theyReceiveRow}>
            <DebounceInput
              minLength={2}
              className={styles.amountSection.theyReceiveInput}
              name="recievingAmount"
              type="text"
              value={formatNumber(calculator?.amountCalculation?.reciepient_recieve)}
              debounceTimeout={1000}
              disabled={calculator?.amountCalculation?.lock_reciepient_recieve}
              onChange={(e: any) => {
                const newAmount = e.target.value.replace(/,/g, "");
                dispatch(
                  remittanceActions.setAmountCalculation({
                    reciepient_recieve: parseNullableNumber(newAmount),
                    changeInputFrom: "receive",
                  })
                );
              }}
              min={55}
            />

            <div
              id="lockIcon"
              className={styles.amountSection.lockIcon}
              onClick={() => {
                dispatch(
                  remittanceActions.setAmountCalculation({
                    lock_reciepient_recieve:
                      !calculator?.amountCalculation?.lock_reciepient_recieve,
                  })
                );
              }}
            >
              {calculator?.amountCalculation?.lock_reciepient_recieve ? (
                <LockIcon />
              ) : (
                <UnlockIcon />
              )}
            </div>

            <SelectDropdown
              data={AppConstants.RemittanceCountryList}
              value={calculator?.amountCalculation?.countryDropdown}
              onChange={(response) => {
                dispatch(
                  remittanceActions.setAmountCalculation({
                    countryDropdown: response,
                    dest_currency: response?.currencyName,
                  })
                );
              }}
              getLabel={(item) => item.countryName}
              getValue={(item) => item.currencyName}
              formatOptionLabel={(item) => (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                  id={item.currencyName}
                  key={item.currencyName}
                >
                  <img
                    src={`/images/png/${item.currencyName}.png`}
                    alt={item.currencyName}
                    width={24}
                    height={24}
                    className={styles.amountSection.selectCurrencyMax}
                  />
                  <div>
                    <div style={{ fontSize: "18px", color: "#000" }}>{item.currencyName}</div>
                  </div>
                </div>
              )}
              width={"130px"}
              currency={true}
            />
          </div>
        </div>

        {/* Purpose Code */}
        <div id="Purpose Code" className={styles.amountSection.purposeContainer}>
          <span className={styles.amountSection.purposeLabel}>
            {AppStrings.AMOUNT_SECTION.PURPOSE_CODE_LABEL}
          </span>

          <SelectDropdown
            data={calculator?.purpose?.list ?? []}
            value={calculator?.amountCalculation?.purposeDropdown ?? null}
            onChange={(response) => {
              if (response?.purposeCode === "S0305") {
                dispatch(remittanceActions.setModalType("LOAN_DISBURSEMENT"));
                dispatch(
                  remittanceActions.setAmountCalculation({
                    purposeDropdown: response,
                    purposeCode: response?.purposeCode,
                    transaction_purpose: response?.transactionPurpose,
                    isBene: false,
                  })
                );
              } else {
                dispatch(
                  remittanceActions.setAmountCalculation({
                    purposeDropdown: response,
                    purposeCode: response?.purposeCode,
                    transaction_purpose: response?.transactionPurpose,
                    isEducation: false,
                    isBene: true,
                    roundOff: false,
                  })
                );
              }
            }}
            getLabel={(item) => item.transactionPurpose}
            getValue={(item) => item.transactionPurpose}
            width={"370px"}
            backgroundColor="#dbeafe"
            placeholder={AppStrings.AMOUNT_SECTION.SELECT_PURPOSE}
            isSearchable={false}
            isLoading={calculator?.purpose?.loading}
          />
          {calculator?.amountCalculation?.transaction_purpose?.toLowerCase()?.includes("family") ||
          calculator?.amountCalculation?.transaction_purpose
            ?.toLowerCase()
            ?.includes("donation") ? (
            <span className="text-red-200 ml-[1px] text-wrap max-w-[370px]">
              {AppStrings.AMOUNT_SECTION.LESS_AMOUNT_WARNING}
            </span>
          ) : null}
        </div>

        {/* Exchange Rate */}
        <div id="Enter Rate" className={styles.amountSection.rateContainer}>
          <p className={styles.amountSection.rateLabel}>
            {AppStrings.AMOUNT_SECTION.EXCHANGE_RATE_LABEL}{" "}
            <span className={styles.amountSection.rateSubText}>
              {calculator?.amountCalculation?.transaction_purpose &&
                `(Card rate: 1 ${calculator?.amountCalculation?.dest_currency} → ${calculator?.cardRateByCurrencyPurpose} INR)`}
            </span>
          </p>

          <div
            className={`${styles.amountSection.rateInputContainer} ${
              !calculator?.amountCalculation?.transaction_purpose ? "opacity-15" : ""
            }`}
          >
            <DebounceInput
              minLength={2}
              className={styles.amountSection.rateInput}
              name="recievingAmount"
              type="number"
              value={calculator?.amountCalculation?.conversion_rate}
              debounceTimeout={1000}
              onChange={(e: any) => {
                let { value } = e.target;
                if (value === "") {
                  dispatch(remittanceActions.setAmountCalculation({ conversion_rate: "" }));
                  toast.error("Please enter a valid rate");
                  return;
                }
                const numericValue = Number(value);
                if (isNaN(numericValue) || numericValue < 0) {
                  toast.error("Please enter a valid rate");
                  return;
                }
                dispatch(remittanceActions.setAmountCalculation({ conversion_rate: numericValue }));
              }}
              onInput={(e: any) => {
                if (e.target.value === "")
                  dispatch(remittanceActions.setAmountCalculation({ conversion_rate: "" }));
              }}
              fullWidth
              inputClasses="text-[20px] bg-blue-100"
              disabled={!calculator?.amountCalculation?.transaction_purpose}
            />
          </div>

          {!calculator?.amountCalculation?.transaction_purpose && (
            <span
              className={styles.amountSection.rateWarning}
              style={styles.amountSection.rateWarningStyle}
            >
              {AppStrings.AMOUNT_SECTION.SELECT_PURPOSE_TO_ENABLE_RATE}
            </span>
          )}
        </div>

        {/* Vendor Code */}
        <div id="Vendor Code" className={styles.amountSection.vendorContainer}>
          <span className={styles.amountSection.vendorLabel}>
            {AppStrings.AMOUNT_SECTION.VENDOR_CODE_LABEL}
          </span>

          <SelectDropdown
            data={calculator?.vendorCode?.list ?? []}
            value={calculator?.amountCalculation?.vendorDropdown ?? null}
            onChange={(response) => {
              if (response) {
                dispatch(
                  remittanceActions.setAmountCalculation({
                    vendorDropdown: response,
                    transVendorKey: response?.userId,
                    referrerVendor: response?.vendorCode,
                  })
                );
              } else {
                dispatch(
                  remittanceActions.setAmountCalculation({
                    vendorDropdown: null,
                    transVendorKey: "",
                    referrerVendor: "",
                  })
                );
              }
            }}
            getLabel={(item) => item.displayName ?? item.vendorCode}
            getValue={(item) => item.userId}
            width={"370px"}
            backgroundColor="#dbeafe"
            placeholder={"Select vendor code"}
            isClearable={true}
            isLoading={calculator?.vendorCode?.loading}
          />

          {/* {calculator?.getTransactionSummary?.error?.type && (
            <span className="text-red-200 font-normal text-[14px]">
              {calculator?.getTransactionSummary?.error?.type}
            </span>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default AmountSection;
